/**
 * Keep a portalled panel pinned to its trigger while it is open.
 *
 * The listeners are the whole reason this is a hook rather than a call to
 * `placeAnchoredPanel`: a panel measured once and then left alone drifts away
 * from its anchor the moment anything scrolls or the window resizes.
 */
import { RefObject, useCallback, useLayoutEffect, useState } from 'react';
import { AnchoredPos, placeAnchoredPanel } from './anchoredPanel';

export default function useAnchoredPanel(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  width: number,
): AnchoredPos | null {
  const [pos, setPos] = useState<AnchoredPos | null>(null);

  const place = useCallback(() => {
    const r = anchorRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos(placeAnchoredPanel(r, width, window.innerWidth, window.innerHeight));
  }, [anchorRef, width]);

  useLayoutEffect(() => {
    // Measured before paint, so the panel never appears in the wrong place
    // first and then jumps.
    if (!open) {
      setPos(null);
      return;
    }
    place();
    window.addEventListener('resize', place);
    // CAPTURE phase. The trigger usually sits inside a scrolling pane, and a
    // scroll on that pane never reaches window on the bubble phase — the panel
    // would hang in mid-air while the trigger slid out from under it.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  return pos;
}
